import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ConfigurationService } from "providers/configuration/configuration.service";
import { Observable } from "rxjs";

export interface PreVisitSummaryResult {
  audioUrl: string;
  transcript: string;
  s3FolderKey: string;
  audioKey: string;
}

export interface CreatePreVisitSummaryResult {
  id: number;
  message: string;
  success: boolean;
}

export interface GetAssessmentIdsResult {
  patientId: number;
  assessmentIds: number[];
}

export interface GenerateCareSummaryResult {
  careSummaryId: number;
  status: string;
  message: string;
}

export interface CareSummaryStatusResult {
  status: string | null;
  last_synced_at: string | null;
}

export interface CareSummaryDetail {
  id: number;
  patientChartId: number;
  summaryText: string;
  audioS3Key: string;
  pdfS3Key: string;
  rating: number;
  status: string;
  lastSyncedAt: string;
  createdDte: string;
}

export interface PresignedUrlResult {
  url: string;
  expires_in: number;
}

@Injectable()
export class PatientPreVisitSummaryService {
  private apiUrl: string;
  private mock: boolean = false;
  constructor(private http: HttpClient, private config: ConfigurationService) {
    this.apiUrl = config.getConfig().apiUrl;

    if (
      this.config.getConfig().isLocal &&
      this.config.getConfig().localEndpoints.patientPreVisitSummary
    ) {
      this.apiUrl =
        this.config.getConfig().localEndpoints.patientPreVisitSummary;
    } else {
      this.apiUrl = this.config.getConfig().apiUrl + "/patient/";
    }
  }

  public getPreVisitSummary(
    usecase: string,
    files: File[],
    patientId: string
  ): Observable<PreVisitSummaryResult> {
    const formData = new FormData();
    formData.append("usecase", usecase);

    files.forEach((file) => formData.append("file", file));

    return this.http.post<PreVisitSummaryResult>(
      this.config.getConfig().apiUrl +
        "/curantis-gateway/murphi/summarize/" +
        patientId,
      formData
    );
  }

  public createSummary(
    patientId: string,
    summaryData: {
      summaryText: string;
      audioS3Key: string;
      rating: number;
      // tenantId: number;
      summaryRequest: {
        assessmentId: number[];
        pdfS3Key: string;
      };
    }
  ): Observable<CreatePreVisitSummaryResult> {
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    return this.http.post<CreatePreVisitSummaryResult>(
      this.config.getConfig().apiUrl + "/care-summary/patient/" + patientId,
      summaryData,
      { headers }
    );
  }
  public getAssessmentIds(
    patientId: string
  ): Observable<GetAssessmentIdsResult> {
    return this.http.get<GetAssessmentIdsResult>(
      this.config.getConfig().apiUrl +
        "/patient/" +
        patientId +
        "/assessments-for-summary"
    );
  }

  public generateCareSummary(
    patientChartId: number
  ): Observable<GenerateCareSummaryResult> {
    return this.http.post<GenerateCareSummaryResult>(
      this.config.getConfig().apiUrl + "/care-summary/generate",
      { patientChartId }
    );
  }

  public getCareSummaryStatus(
    patientChartId: number
  ): Observable<CareSummaryStatusResult> {
    return this.http.get<CareSummaryStatusResult>(
      this.config.getConfig().apiUrl + "/care-summary/status/" + patientChartId
    );
  }

  public getCareSummaries(
    patientChartId: number
  ): Observable<CareSummaryDetail[]> {
    return this.http.get<CareSummaryDetail[]>(
      this.config.getConfig().apiUrl + "/care-summary/patient/" + patientChartId
    );
  }

  // Care summary audio is stored in the gateway S3 bucket; the browser can't play the
  // raw s3:// key, so mint a short-lived presigned URL on demand.
  public getPresignedUrl(s3Key: string): Observable<PresignedUrlResult> {
    return this.http.get<PresignedUrlResult>(
      this.config.getConfig().apiUrl + "/curantis-gateway/s3/presigned-url",
      { params: { s3Key } }
    );
  }
}



// WEBPACK FOOTER //
// ./src/providers/patient-charts/patient-pre-visit-summary-service.ts